import { cross, normalize, dot, scale, sub, type Vec3 } from '@rcs/domain';
import type { SimulationResult } from './simulate';
import type { SimulationFrame } from './types';

function mix(a: Vec3, b: Vec3, t: number): Vec3 {
  return [a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t, a[2] + (b[2] - a[2]) * t];
}

/**
 * 時刻 `time` [s] の結果フレームを、前後のフレームから補間して返す（描画のフレームレートに依存しない再生のため）。
 * 位置・速度・G などは線形補間し、姿勢は接線・上方向を補間して直交化し直す。
 * 範囲外の時刻は、最初・最後のフレームに丸める。
 *
 * @throws {RangeError} フレームが空の場合
 */
export function frameAtTime(result: SimulationResult, time: number): SimulationFrame {
  const frames = result.frames;
  const first = frames[0];
  const last = frames[frames.length - 1];
  if (first === undefined || last === undefined) throw new RangeError('結果にフレームがありません');
  if (time <= first.time) return first;
  if (time >= last.time) return last;
  // time を挟む 2 フレームを二分探索で求める
  let low = 0;
  let high = frames.length - 1;
  while (high - low > 1) {
    const middle = (low + high) >> 1;
    if ((frames[middle]?.time ?? 0) <= time) low = middle;
    else high = middle;
  }
  const a = frames[low];
  const b = frames[high];
  if (a === undefined || b === undefined) return last;
  const span = b.time - a.time;
  const f = span > 0 ? (time - a.time) / span : 0;
  const tangent = normalize(mix(a.orientation.tangent, b.orientation.tangent, f));
  const rawUp = mix(a.orientation.up, b.orientation.up, f);
  const up = normalize(sub(rawUp, scale(tangent, dot(rawUp, tangent))));
  return {
    time,
    s: a.s + (b.s - a.s) * f,
    speed: a.speed + (b.speed - a.speed) * f,
    acceleration: a.acceleration + (b.acceleration - a.acceleration) * f,
    position: mix(a.position, b.position, f),
    orientation: { tangent, up, right: cross(tangent, up) },
    gForce: {
      vertical: a.gForce.vertical + (b.gForce.vertical - a.gForce.vertical) * f,
      lateral: a.gForce.lateral + (b.gForce.lateral - a.gForce.lateral) * f,
      longitudinal: a.gForce.longitudinal + (b.gForce.longitudinal - a.gForce.longitudinal) * f,
    },
  };
}
